import { Modal } from 'ui/modals'
import { useEffect, useMemo, useState, Suspense } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

import config from 'config/next-seo.config'
import { useGetStyleBySlug } from '@/lib/graphql/realthread/queries'
import { Footer } from 'ui'
import { useCart } from 'hooks/graphql/legacy-api'
import ProductModalSkeleton from '@/components/modals/ProductModalSkeleton'
import { usePriceCalculatorById } from 'hooks'
import { NextSeo } from 'next-seo'
import { getColorName } from '@/lib/utils/products'
import { PrismicDocument } from '@prismicio/types'

const SingleProductPage = dynamic(() => import('../product/SingleProductPage'), {
	ssr: false
})

type AnalyticsCallback = (_data: any, _type?: string) => void

/**
 * ModalProduct Component
 *
 * A modal component that displays detailed product information in a full-screen overlay.
 * It handles product data fetching, SEO optimization, and state management for displaying
 * product details within a modal context.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.originPath] - The path to return to when closing the modal
 * @param {PrismicDocument} [props.globalData] - Global site data from Prismic CMS
 * @param {AnalyticsCallback} [props.analyticsCallback] - Callback function for tracking analytics
 *
 * Features:
 * - Lazy loads the SingleProductPage component for better performance
 * - Manages product style data based on URL slug
 * - Handles SEO metadata including dynamic titles and OpenGraph images
 * - Integrates with Prismic CMS for content management
 * - Provides loading states with skeleton UI
 * - Supports analytics tracking
 * - Manages modal state and navigation

 */

export default function ModalProduct({
	originPath,
	globalData,
	analyticsCallback
}: Readonly<{
	originPath?: string
	globalData?: PrismicDocument
	analyticsCallback?: AnalyticsCallback
}>) {
	// Router instance for handling navigation and URL parameters
	const router = useRouter()
	const { loading: cartLoading } = useCart()

	// State management for product and UI data
	const [selectedStyleSlug, setSelectedStyleSlug] = useState<string | null>(null)
	const { style, loading: styleLoading } = useGetStyleBySlug(selectedStyleSlug)
	const { loading: priceLoading } = usePriceCalculatorById()

	// Aggregate loading state for all async operations
	const isLoading = cartLoading || styleLoading || priceLoading

	/**
	 * Updates selected style slug when URL parameters change
	 * Handles both single and array-based slug parameters
	 */
	useEffect(() => {
		if (router.query.slug && router.query.modal === 'true') {
			const slug = Array.isArray(router.query.slug) ? router.query.slug[0] : router.query.slug
			setSelectedStyleSlug(slug)
		}
	}, [router.query.modal, router.query.slug])

	/**
	 * Handles modal visibility state
	 * When closing, navigates back or to origin path while maintaining scroll position
	 */
	const setShowModal = (isOpen: boolean) => {
		if (!isOpen) {
			if (originPath) {
				router.push(originPath, undefined, { shallow: true, scroll: false })
			} else {
				router.back()
			}
		}
	}

	/**
	 * Memoized values for performance optimization
	 */
	// Get color name from URL parameter
	const colorName = useMemo(() => getColorName(router.query.color as string), [router.query.color])

	// Generate product title including manufacturer, name, and color if available
	const title = useMemo(() => {
		if (!style) return ''

		const baseName = style.manufacturer
			? `${style.manufacturer} ${style.name} - ${style.detail}`
			: style.metaFullName

		return colorName ? `${baseName} in ${colorName}` : baseName
	}, [style, colorName])

	// Construct OpenGraph image object for social sharing
	const og_image_url = useMemo(() => {
		return {
			url:
				style?.colors?.find((color) => color.name === colorName)?.frontLargeImageUrl ??
				style?.colors?.[0]?.frontLargeImageUrl,
			alt: 'high-quality custom t-shirts, totes, hats, and more',
			copyright: null,
			dimensions: { width: 1011, height: 759 },
			edit: { x: 0, y: 0, zoom: 1, background: 'transparent' }
		}
	}, [style, colorName])

	/**
	 * Triggers analytics callback when style data is loaded
	 */
	useEffect(() => {
		if (analyticsCallback) {
			analyticsCallback(style, 'Modal View')
		}
	}, [style, analyticsCallback])

	return (
		selectedStyleSlug && (
			<>
				{/* SEO Configuration */}
				<NextSeo
					title={title}
					description={style?.metaDescription}
					openGraph={{
						images: og_image_url?.url
							? [og_image_url]
							: globalData?.data.og_image?.url
							? [globalData?.data.og_image]
							: undefined
					}}
					{...config}
				/>
				{/* Modal Container */}
				<Modal
					isOpen={!!router.query.modal}
					setIsOpen={setShowModal}
					afterLeave={() => setSelectedStyleSlug(null)}
					modalClassName="!p-0 !items-end"
					panelClassName="h-full min-w-full !rounded-b-none !p-0 sm:!max-h-[calc(100vh_-_23px)]"
					aria-label="Product Details"
				>
					{/* Lazy-loaded Content */}
					<Suspense>
						{isLoading ? (
							<ProductModalSkeleton />
						) : (
							<SingleProductPage style={style} isFromBuilderModal globalData={globalData} />
						)}
					</Suspense>
					{/* Footer Section */}
					{globalData && <Footer showMarketingSection showSearchAndLinks globalData={globalData} />}
				</Modal>
			</>
		)
	)
}
